import type {
  MetaGoalBucket,
  MetaGoalCostPer,
  MetaGoalCounts,
  MetaGoalMoney,
  PortalMetaGoalHeadline,
  PortalMetaGoalPanel,
  PortalMetaGoalPanelSeries,
  PortalMetaGoalReport,
  PortalCoverage,
  PortalGrouping,
} from '../../types/portalReport.js';
import { META_GOAL_BUCKETS, isMetaGoalBucket } from '../../types/portalReport.js';
import { buildSeries, type MeasuredBucket } from './series.js';
import { yearOnYearMonths } from './yearOnYear.js';

// ⚠️⚠️ THE GOAL-SPLIT META REPORT (2026-09-24) — the pure core. PURE: no I/O.
//
// Scott: "If we just combine our normal stats for 'Total ad Conversions' its
// going to make the data look bad and not a true reflection of the activity."
// A brand running on-Meta lead forms, website lead campaigns, purchase campaigns
// and landing-page-view campaigns gets ONE conversions figure at the top and one
// panel per campaign goal beneath it.
//
// ⚠️⚠️ THE RULE THAT MAKES IT RECONCILE (Scott): every panel shows EVERY result
// its campaigns produced; COST-PER is shown ONLY for that panel's own goal. An
// extra is a number, not an efficiency metric. Because every result is counted
// in exactly one panel, leads / purchases / landing page views / spend each sum
// across the four panels to the headline — BY CONSTRUCTION, and `reconcile`
// below checks it against an INDEPENDENTLY-queried headline rather than one
// computed from the panels (which could not fail).
//
// ⚠️ WHICH CAMPAIGN IS IN WHICH PANEL is decided in
// server/modules/reporting/query/campaignClass.ts and nowhere else. This file
// decides only what each panel SHOWS.
//
// ⚠️ ROAS APPEARS NOWHERE, deliberately (Scott). O-Studio's purchase values are
// sound; ROAS is meaningless while conversion tracking under-reports, which it
// currently does. `purchaseValue` is kept, so re-adding ROAS later is a display
// change rather than a data one.
//
// ⚠️ NOTHING HERE NAMES A BRAND OR AN ACCOUNT. Which scopes get this report is
// `metaGoalReportEnabled` below, resolved from the brand relationship.

// ---------------------------------------------------------------------------
// The panel spec — the ONE place a panel's contents are defined. Kept as data so
// iteration over the coming months is an edit here, not a component rewrite.
// ---------------------------------------------------------------------------

/**
 * ⚠️⚠️ REACH WAS CONSIDERED AND REMOVED (2026-09-25) — do not re-add it as an
 * obvious gap. It was an error in the brief, not an implementation shortcut:
 * reach counts UNIQUE PEOPLE, so summing daily (or per-campaign) values
 * double-counts anyone who saw an ad more than once, and the figure is wrong in
 * the FLATTERING direction. The reporting rules forbid summing it anywhere.
 * Other Campaigns keeps landing page views + cost per landing page view;
 * Uncategorised keeps leads, purchases and landing page views.
 */
export type MetaGoalCountKey = keyof MetaGoalCounts;

export interface MetaGoalPanelSpec {
  bucket: MetaGoalBucket;
  title: string;
  /** The panel's own goal counts, in display order. */
  primary: readonly MetaGoalCountKey[];
  /** The cost-per figures shown — each is spend over one of `primary`. */
  costPer: readonly MetaGoalCostPer[];
  /** Whether purchase value is a primary figure. */
  showPurchaseValue: boolean;
  /** Numbers shown WITHOUT a cost-per. */
  extras: readonly MetaGoalCountKey[];
  /** One line of client-facing copy, rendered whenever the panel has any spend or result. */
  note: string | null;
  /**
   * The count the panel's chart plots, or null for no chart.
   *
   * ⚠️ UNCATEGORISED IS NULL ON PURPOSE (Scott, round 2). It keeps its note, so
   * the absence reads as deliberate rather than as a chart that failed to load.
   */
  chart: MetaGoalCountKey | null;
}

/**
 * ⚠️⚠️ CLIENT-FACING, VERBATIM FROM THE BRIEF. Do NOT reword it to say Meta is
 * not reporting the objective: measured 2026-09-24, the Meta API returns it
 * correctly for these exact campaigns — our data feed (Zernio) does not pass it
 * through. Naming Meta would be false, and it is copy a franchisee will repeat.
 */
export const UNCATEGORISED_NOTE =
  "Campaign objective isn't available in our data feed for these campaigns, so they can't be grouped by goal. All spend and results are still counted.";

// ⚠️ THE PURCHASE PANEL'S "shared spend" NOTE WAS REMOVED ON SCOTT'S INSTRUCTION
// (round 2, 2026-09-25). It said cost per purchase and cost per landing page view
// each divide the WHOLE spend rather than splitting it, and existed to stop a
// reader adding the two and concluding spend was double-counted. The panel layout
// is now judged to carry that on its own. If the misreading turns up in a client
// conversation, the sentence is the thing to restore.

/**
 * ⚠️⚠️ SHOWN ON THE PANEL CHARTS, because the fault it pre-empts is real and
 * looks like a bug. Zernio drops the campaign objective for some campaigns and a
 * re-pull cannot recover it (finding 2026-09-24-zernio-drops-campaign-objective),
 * and those campaigns land in Uncategorised — which has no chart. There are more
 * of them further back, so going back in time these charts can dip while the
 * activity did not.
 */
export const GOAL_CHART_HISTORY_NOTE =
  "Campaigns we can't group by goal are counted in Uncategorised, which has no chart. There are more of those further back in time, so earlier months here can read lower than the activity that ran.";

export const META_GOAL_PANELS: readonly MetaGoalPanelSpec[] = [
  {
    bucket: 'lead_gen',
    title: 'Direct on Meta Lead Gen',
    primary: ['leads'],
    costPer: ['costPerLead'],
    showPurchaseValue: false,
    extras: ['purchases', 'landingPageViews'],
    note: null,
    chart: 'leads',
  },
  {
    bucket: 'purchase',
    title: 'Meta Purchase Campaigns',
    primary: ['purchases', 'landingPageViews'],
    costPer: ['costPerPurchase', 'costPerLandingPageView'],
    showPurchaseValue: true,
    extras: ['leads'],
    note: null,
    chart: 'purchases',
  },
  {
    bucket: 'other',
    title: 'Other Campaigns',
    // ⚠️ Website lead campaigns (OUTCOME_LEADS + OFFSITE_CONVERSIONS) live here
    // too; their leads are an EXTRA. An earlier decision gave Other a cost per
    // lead — Scott's cost-per rule (2026-09-24) supersedes it.
    // ⚠️ IMPRESSIONS are a primary FIGURE (round 2) with no cost-per — cost per
    // impression is not a goal anyone set these campaigns up for.
    primary: ['landingPageViews', 'impressions'],
    costPer: ['costPerLandingPageView'],
    showPurchaseValue: false,
    extras: ['leads', 'purchases'],
    note: null,
    chart: 'landingPageViews',
  },
  {
    bucket: 'uncategorised',
    title: 'Uncategorised',
    // ⚠️ NO COST-PER AT ALL — there is no known goal to divide by, and that
    // absence is the honest signal.
    primary: [],
    costPer: [],
    showPurchaseValue: false,
    extras: ['leads', 'purchases', 'landingPageViews'],
    note: UNCATEGORISED_NOTE,
    chart: null,
  },
];

/** Which count each cost-per divides by. */
export const COST_PER_DENOMINATOR: Record<MetaGoalCostPer, MetaGoalCountKey> = {
  costPerLead: 'leads',
  costPerPurchase: 'purchases',
  costPerLandingPageView: 'landingPageViews',
};

/**
 * The cost-per whose denominator IS the panel's chart metric, or null.
 *
 * ⚠️ DERIVED, not a fourth spec field to keep in sync — and derived from the
 * CHART metric, not "the first cost-per": the Purchase panel lists both cost per
 * purchase and cost per landing page view, and its chart plots purchases.
 */
export function chartCostPerFor(spec: MetaGoalPanelSpec): MetaGoalCostPer | null {
  if (spec.chart === null) return null;
  return spec.costPer.find((k) => COST_PER_DENOMINATOR[k] === spec.chart) ?? null;
}

export function panelSpecFor(bucket: MetaGoalBucket): MetaGoalPanelSpec {
  const spec = META_GOAL_PANELS.find((p) => p.bucket === bucket);
  if (!spec) throw new Error(`metaGoalReport: no panel spec for ${bucket}`);
  return spec;
}

// ---------------------------------------------------------------------------
// Rows in
// ---------------------------------------------------------------------------

/** One (bucket, currency) row off the panels query. */
export interface MetaGoalBucketRow {
  bucket: MetaGoalBucket;
  currency: string;
  spend: number;
  leads: number;
  purchases: number;
  purchaseValue: number;
  landingPageViews: number;
  impressions: number;
}

/** One currency of the headline query — the witness. */
export interface MetaConversionTotalsRow {
  currency: string;
  spend: number;
  leads: number;
  purchases: number;
  landingPageViews: number;
  impressions: number;
}

/**
 * One (day, bucket, currency) row off the panel-series query. Counts fold across
 * currencies in the assembly; spend never does.
 */
export interface MetaGoalSeriesRow {
  date: string;
  bucket: MetaGoalBucket;
  currency: string;
  spend: number;
  leads: number;
  purchases: number;
  landingPageViews: number;
  impressions: number;
}

/**
 * A bucket string off the wire. ⚠️ An unrecognised value lands in `other` — the
 * safe default — rather than being dropped, which would break the
 * reconciliation silently. (The SQL CASE can only emit the four keys; this is
 * the second line.)
 */
export function bucketOrOther(v: unknown): MetaGoalBucket {
  return isMetaGoalBucket(v) ? v : 'other';
}

// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------

const round2 = (n: number): number => Math.round(n * 100) / 100;

function emptyCounts(): MetaGoalCounts {
  return { leads: 0, purchases: 0, landingPageViews: 0, impressions: 0 };
}

/** Cost per X — null for X = 0 (never a cost-per of nothing, never ∞). */
export function costPerOrNull(spend: number, count: number): number | null {
  return count > 0 ? round2(spend / count) : null;
}

/**
 * Build the four panels. ⚠️ ALWAYS FOUR, in panel order: a bucket with no rows
 * is zeros rather than absent, so the page layout does not jump between
 * periods and the reconciliation always has four terms.
 */
export function buildMetaGoalPanels(rows: readonly MetaGoalBucketRow[]): PortalMetaGoalPanel[] {
  return META_GOAL_BUCKETS.map((bucket) => {
    const spec = panelSpecFor(bucket);
    const mine = rows.filter((r) => r.bucket === bucket);
    const counts = emptyCounts();
    const byCurrency = new Map<string, MetaGoalBucketRow>();
    for (const r of mine) {
      counts.leads += r.leads;
      counts.purchases += r.purchases;
      counts.landingPageViews += r.landingPageViews;
      counts.impressions += r.impressions;
      const cur = byCurrency.get(r.currency);
      byCurrency.set(
        r.currency,
        cur
          ? {
              ...cur,
              spend: cur.spend + r.spend,
              leads: cur.leads + r.leads,
              purchases: cur.purchases + r.purchases,
              purchaseValue: cur.purchaseValue + r.purchaseValue,
              landingPageViews: cur.landingPageViews + r.landingPageViews,
              impressions: cur.impressions + r.impressions,
            }
          : { ...r },
      );
    }
    // ⚠️ MONEY IS PER CURRENCY, and each cost-per divides a currency's spend by
    // THAT currency's own count — never a blended figure.
    const money: MetaGoalMoney[] = [...byCurrency.values()]
      .sort((a, b) => b.spend - a.spend)
      .map((c) => {
        const costPer: MetaGoalMoney['costPer'] = {};
        for (const k of spec.costPer) {
          costPer[k] = costPerOrNull(c.spend, c[COST_PER_DENOMINATOR[k]]);
        }
        return {
          currency: c.currency,
          spend: round2(c.spend),
          purchaseValue: round2(c.purchaseValue),
          costPer,
        };
      });
    return { bucket, counts, money, series: null };
  });
}

export interface HeadlineInput {
  totals: readonly MetaConversionTotalsRow[];
}

/**
 * ⚠️⚠️ META ONLY (round 2, 2026-09-25). Google left the Meta page entirely, and
 * it took its headline contribution with it — a headline of 60 above stats
 * summing to 59 was the defect. `googleHeadlineConversions` /
 * `googleHeadlineNote` went with it; there is no Google input to this function.
 */
export function buildMetaGoalHeadline(input: HeadlineInput): PortalMetaGoalHeadline {
  let metaLeads = 0;
  let metaPurchases = 0;
  let metaLandingPageViews = 0;
  let metaImpressions = 0;
  const spend = new Map<string, number>();
  for (const t of input.totals) {
    metaLeads += t.leads;
    metaPurchases += t.purchases;
    metaLandingPageViews += t.landingPageViews;
    metaImpressions += t.impressions;
    spend.set(t.currency, (spend.get(t.currency) ?? 0) + t.spend);
  }
  return {
    metaLeads,
    metaPurchases,
    metaLandingPageViews,
    metaImpressions,
    total: round2(metaLeads + metaPurchases),
    metaSpend: [...spend.entries()]
      .map(([currency, s]) => ({ currency, spend: round2(s) }))
      .sort((a, b) => b.spend - a.spend),
  };
}

// ---------------------------------------------------------------------------
// The panel charts (round 2, 2026-09-25)
// ---------------------------------------------------------------------------

export interface GoalSeriesOptions {
  /** The selected period. */
  range: { from: string; to: string };
  grouping: PortalGrouping;
  /** The client's own today. */
  today: string;
  /** The scope's Meta coverage — the SAME one the page's other Meta charts use. */
  coverage: PortalCoverage;
}

/** The first day of the twelve-month window the panel charts draw. */
function monthsWindowFrom(today: string): string {
  return `${yearOnYearMonths(today)[0] as string}-01`;
}

/** The date range the panel-series query must cover: the period ∪ twelve months. */
export function metaGoalSeriesRange(
  range: { from: string; to: string },
  today: string,
): { from: string; to: string } {
  const monthsFrom = monthsWindowFrom(today);
  return {
    from: range.from < monthsFrom ? range.from : monthsFrom,
    to: range.to > today ? range.to : today,
  };
}

/**
 * Each charted panel's series, for the period and for the last twelve months.
 *
 * ⚠️⚠️ A 12-MONTH VIEW IS A QUERY, NOT A RENDERING. The rows arrive already
 * tagged with the campaign's bucket, resolved ONCE per campaign over the
 * account (campaignClass.ts) — the same resolution the panels use — so a
 * campaign sits in the same panel in every month it ran.
 *
 * ⚠️ Built by the SHARED `buildSeries`, so an unmeasured month is null and a
 * measured month with nothing in it is a real 0 — the report's rule, not a copy
 * of it. The count rides in `leads`; see `PortalMetaGoalPanel.series`.
 *
 * ⚠️⚠️ THE COST-PER IS NOT COMPUTED HERE (2026-09-25). Real spend is carried onto
 * each MeasuredBucket and `buildSeries` derives `costPerLead` PER BUCKET from
 * that bucket's own spend and count — never an average of daily cost-pers — and
 * returns null for spend with none of the count (never a "free" $0.00). The
 * value rides in `costPerLead` whatever the cost-per really is; `costPer` on the
 * series says which.
 *
 * ⚠️ A panel whose rows span MORE THAN ONE CURRENCY carries no money at all —
 * spend and cost-per nulled, `costPer: null` — rather than a blended line.
 */
export function buildMetaGoalPanelSeries(
  rows: readonly MetaGoalSeriesRow[],
  opts: GoalSeriesOptions,
): Partial<Record<MetaGoalBucket, PortalMetaGoalPanelSeries>> {
  const monthsFrom = monthsWindowFrom(opts.today);
  const out: Partial<Record<MetaGoalBucket, PortalMetaGoalPanelSeries>> = {};
  for (const spec of META_GOAL_PANELS) {
    const metric = spec.chart;
    if (metric === null) continue;
    const mine = rows.filter((r) => r.bucket === spec.bucket);
    const currencies = new Set(mine.map((r) => r.currency));
    // ⚠️ One currency (or none yet) ⇒ money may be charted; two ⇒ it may not.
    const singleCurrency = currencies.size <= 1;
    const costPer = singleCurrency ? chartCostPerFor(spec) : null;
    const measured: MeasuredBucket[] = mine.map((r) => ({
      bucket: r.date,
      leads: r[metric],
      // ⚠️ REAL spend, so buildSeries derives the cost-per. Zero only when the
      // chart may not carry money, and then it is nulled again below — a zero
      // spend on the wire would read as a real one.
      spend: costPer ? r.spend : 0,
      impressions: 0,
      clicks: 0,
    }));
    const within = (from: string, to: string) =>
      measured.filter((m) => m.bucket >= from && m.bucket <= to);
    const shape = (points: ReturnType<typeof buildSeries>) =>
      points.map((p) => ({
        ...p,
        // ⚠️ Delivery counts are not charted here (not in scope), so not carried.
        impressions: null,
        clicks: null,
        ...(costPer ? {} : { spend: null, costPerLead: null }),
      }));
    out[spec.bucket] = {
      metric,
      costPer,
      currency: costPer ? ([...currencies][0] ?? null) : null,
      period: shape(
        buildSeries({
          from: opts.range.from,
          to: opts.range.to,
          grouping: opts.grouping,
          measured: within(opts.range.from, opts.range.to),
          coverage: opts.coverage,
          today: opts.today,
        }),
      ),
      months: shape(
        buildSeries({
          from: monthsFrom,
          to: opts.today,
          grouping: 'monthly',
          measured: within(monthsFrom, opts.today),
          coverage: opts.coverage,
          today: opts.today,
        }),
      ),
    };
  }
  return out;
}

// ---------------------------------------------------------------------------
// ⚠️⚠️ THE RECONCILIATION — checked, not hoped for.
// ---------------------------------------------------------------------------

export interface ReconcileMismatch {
  measure: 'leads' | 'purchases' | 'landingPageViews' | 'impressions' | 'spend';
  currency: string | null;
  panels: number;
  headline: number;
}

/** Counts are sums of whole events but purchases arrive as FLOAT64. */
const COUNT_TOLERANCE = 1e-6;
/**
 * Spend is rounded to the cent once per panel (four) and once in the headline,
 * so honest rounding can differ by up to 5 × half a cent. A real drop is a whole
 * campaign-day of spend, orders of magnitude above this.
 */
const SPEND_TOLERANCE = 0.03;

/**
 * Σ panels == headline on leads, purchases, landing page views, and spend PER
 * CURRENCY (money is never compared blended). The headline must come from the
 * SEPARATE totals query — passing a headline built from the panels would make
 * this `x == Σx` and it could never fail.
 */
export function reconcileMetaGoalReport(
  panels: readonly PortalMetaGoalPanel[],
  headline: PortalMetaGoalHeadline,
): ReconcileMismatch[] {
  const out: ReconcileMismatch[] = [];
  const sum = (k: MetaGoalCountKey): number => panels.reduce((a, p) => a + p.counts[k], 0);
  const counts: Array<[ReconcileMismatch['measure'] & MetaGoalCountKey, number]> = [
    ['leads', headline.metaLeads],
    ['purchases', headline.metaPurchases],
    ['landingPageViews', headline.metaLandingPageViews],
    ['impressions', headline.metaImpressions],
  ];
  for (const [k, h] of counts) {
    const p = sum(k);
    if (Math.abs(p - h) > COUNT_TOLERANCE) out.push({ measure: k, currency: null, panels: p, headline: h });
  }
  const panelSpend = new Map<string, number>();
  for (const p of panels) {
    for (const m of p.money) panelSpend.set(m.currency, (panelSpend.get(m.currency) ?? 0) + m.spend);
  }
  const currencies = new Set([...panelSpend.keys(), ...headline.metaSpend.map((s) => s.currency)]);
  for (const c of currencies) {
    const p = panelSpend.get(c) ?? 0;
    const h = headline.metaSpend.find((s) => s.currency === c)?.spend ?? 0;
    if (Math.abs(p - h) > SPEND_TOLERANCE) out.push({ measure: 'spend', currency: c, panels: p, headline: h });
  }
  return out;
}

export function buildMetaGoalReport(
  rows: readonly MetaGoalBucketRow[],
  headlineInput: HeadlineInput,
  series: Partial<Record<MetaGoalBucket, PortalMetaGoalPanelSeries>> = {},
): { report: PortalMetaGoalReport; mismatches: ReconcileMismatch[] } {
  // ⚠️ The series are attached AFTER assembly and never read by the
  // reconciliation, which is a check on the period's totals only.
  const panels = buildMetaGoalPanels(rows).map((p) => ({ ...p, series: series[p.bucket] ?? null }));
  const headline = buildMetaGoalHeadline(headlineInput);
  const mismatches = reconcileMetaGoalReport(panels, headline);
  return { report: { headline, panels, reconciles: mismatches.length === 0 }, mismatches };
}

/** Whether a panel has anything to show — drives the Uncategorised note. */
export function panelHasActivity(panel: PortalMetaGoalPanel): boolean {
  return (
    panel.money.some((m) => m.spend > 0) ||
    panel.counts.leads > 0 ||
    panel.counts.purchases > 0 ||
    panel.counts.landingPageViews > 0 ||
    panel.counts.impressions > 0
  );
}

// ---------------------------------------------------------------------------
// ⚠️⚠️ WHO GETS THIS REPORT — the brand relationship, never a list.
// ---------------------------------------------------------------------------

/**
 * True when EVERY account in scope resolves to a brand whose own account has the
 * goal-split flag on.
 *
 * ⚠️ Scott: "its only for O-Studio locations." That MUST mean "accounts under
 * that brand", not a list of today's nine account ids and not the brand's name:
 * a hardcoded list silently leaves the tenth location on the wrong page until a
 * franchisee notices, where the brand relationship gives it the report the day
 * it is added. So the brand comes from `computeBrandAncestorMap` (passed in —
 * the ONE brand resolver), and the decision from a flag stored on the brand's
 * account.
 *
 * ⚠️ ALL-OR-NOTHING, like the restyle flag: a client sees ONE Meta page over
 * every account they hold, so a scope mixing this brand with another gets the
 * ordinary page. ⚠️ An EMPTY scope is false explicitly — `[].every()` is true.
 * ⚠️ An account with NO brand makes the scope false (it cannot be "under" one).
 */
export function metaGoalReportEnabled(
  accountIds: readonly string[],
  brandByAccount: ReadonlyMap<string, string | null>,
  brandHasGoalReport: (brandAccountId: string) => boolean,
): boolean {
  if (accountIds.length === 0) return false;
  return accountIds.every((id) => {
    const brand = brandByAccount.get(id);
    return brand != null && brandHasGoalReport(brand);
  });
}

// ---------------------------------------------------------------------------
// Presentation vocabulary — pure, so every sentence is asserted without jsdom.
// ---------------------------------------------------------------------------

export const META_GOAL_COUNT_LABELS: Record<MetaGoalCountKey, string> = {
  leads: 'Leads',
  purchases: 'Purchases',
  landingPageViews: 'Landing page views',
  impressions: 'Impressions',
};

export const META_GOAL_COST_PER_LABELS: Record<MetaGoalCostPer, string> = {
  costPerLead: 'Cost per lead',
  costPerPurchase: 'Cost per purchase',
  costPerLandingPageView: 'Cost per landing page view',
};

/**
 * The goal filter's options. `all` is the no-filter state.
 *
 * ⚠️⚠️ FOUR, NOT FIVE — UNCATEGORISED IS DELIBERATELY NOT A BUTTON (Scott, round
 * 2). The consequence is that "All Campaigns" is NOT the sum of the other three
 * wherever a location has campaigns with no objective, and nothing on the page
 * may claim it is: `GOAL_FILTER_NOTE` says so instead. The labels are Scott's.
 */
export const META_GOAL_FILTER_OPTIONS: ReadonlyArray<{ value: MetaGoalBucket | 'all'; label: string }> = [
  { value: 'all', label: 'All Campaigns' },
  { value: 'lead_gen', label: 'Meta Lead Gen' },
  { value: 'purchase', label: 'Purchase' },
  { value: 'other', label: 'Other' },
];

/** ⚠️ Stated beside the filter so "All" never reads as a sum of the other three. */
export const GOAL_FILTER_NOTE =
  "All Campaigns also includes campaigns we can't group by goal, so it can be more than the other three added together.";

/**
 * The bucket the filter shows as pressed, or null for All. ⚠️ `uncategorised` is
 * still accepted by the API but has no button; the client never sends it (see
 * useReportState), and if one arrives it reads as unfiltered here.
 */
export function filterableGoalBucket(v: MetaGoalBucket | null | undefined): MetaGoalBucket | null {
  return v === 'lead_gen' || v === 'purchase' || v === 'other' ? v : null;
}

export type CreativeMetric =
  | { kind: 'count'; key: 'leads' | 'purchases' | 'landingPageViews'; label: string }
  | { kind: 'costPer'; key: MetaGoalCostPer; label: string };

/**
 * What the creative grid shows, per goal filter.
 *
 * ⚠️ THE PANELS' OWN RULE: a cost-per only for the goal the campaigns were set
 * up for. Filtered to Purchase, leads and cost per lead are the wrong measures
 * to lead with; unfiltered, the campaigns have mixed goals, so no single
 * cost-per is honest and the grid shows counts only.
 */
export function creativeMetricsFor(bucket: MetaGoalBucket | null): CreativeMetric[] {
  const count = (key: 'leads' | 'purchases' | 'landingPageViews'): CreativeMetric => ({
    kind: 'count',
    key,
    label: META_GOAL_COUNT_LABELS[key],
  });
  const cost = (key: MetaGoalCostPer): CreativeMetric => ({
    kind: 'costPer',
    key,
    label: META_GOAL_COST_PER_LABELS[key],
  });
  switch (bucket) {
    case 'lead_gen':
      return [count('leads'), cost('costPerLead')];
    case 'purchase':
      return [count('purchases'), cost('costPerPurchase')];
    case 'other':
      return [count('landingPageViews'), cost('costPerLandingPageView')];
    case 'uncategorised':
    case null:
      return [count('leads'), count('purchases')];
  }
}

/** The creative grid's intro sentence, naming the measures it shows. */
export function creativeGridIntro(metrics: readonly CreativeMetric[]): string {
  const names = ['Spend', ...metrics.map((m) => m.label.toLowerCase())];
  const last = names[names.length - 1] as string;
  return `${names.slice(0, -1).join(', ')} and ${last} for each creative, with its current delivery status.`;
}

/**
 * Why the audience breakdown shows nothing under a goal filter, or null.
 *
 * ⚠️ The breakdown counts LEADS. Filtered to Purchase or Other there are often
 * none, and a column of dashes would be indistinguishable from the panel
 * breaking — so the absence is stated in words.
 */
export function filteredAudienceEmptyMessage(
  bucket: MetaGoalBucket | null,
  leadsInBreakdown: number,
): string | null {
  if (bucket === null || leadsInBreakdown > 0) return null;
  const option = META_GOAL_FILTER_OPTIONS.find((o) => o.value === bucket)?.label ?? 'these';
  return `No leads came from ${option} campaigns in this period, so there is no audience breakdown to show for this filter. The breakdown counts leads, not purchases or landing page views.`;
}

/**
 * ⚠️⚠️ TEMPORARY, AND IT COMES DOWN WITH A DEPLOY (Scott's call, 2026-09-25,
 * against the editable-field recommendation).
 *
 * REMOVE WHEN: Mindbody ships its fix AND the goal-split brand's conversion
 * tracking has been verified working again. It is on the standing flags list
 * so it does not survive to Christmas by default.
 *
 * ⚠️ SCOTT'S WORDING, VERBATIM. "has degraded" (plural subject) and "Early
 * August" were both flagged to him and left as written — client-facing copy
 * carrying Kyck's name, his call. Do not "fix" the grammar.
 *
 * ⚠️ Shown only where `goalSplitReport` is true — never on another brand's report.
 */
export const TRACKING_NOTICE =
  'Website tracking and analytics has degraded since the latest Mindbody update in Early August, some conversion metrics will not be accurate until Mindbody rolls out a fix. We are actively working with them to resolve.';
